import React, { useState, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Pencil, Trash2, Check, X, Image, Film, Music, FolderOpen, AlertTriangle } from 'lucide-react';
import type { Workflow, Project } from '../../../types';

interface CanvasCardProps {
  project: Project;
  workflow?: Workflow;
  isEditing: boolean;
  editingTitle: string;
  // 回调签名统一接收 id：让 CanvasGallery 可用 useCallback 稳定引用，
  // 避免 4 个内联箭头函数在每次渲染时生成新引用击穿 memo
  onOpen: (id: string) => void;
  onRenameStart: (id: string, name: string) => void;
  onRenameChange: (value: string) => void;
  onRenameSave: (id: string) => void;
  onRenameCancel: () => void;
  onDelete: (id: string) => void;
}

const CanvasCardComponent: React.FC<CanvasCardProps> = ({
  project,
  workflow,
  isEditing,
  editingTitle,
  onOpen,
  onRenameStart,
  onRenameChange,
  onRenameSave,
  onRenameCancel,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const handleOpenMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = menuButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 128 });
    }
    setMenuOpen(true);
  };

  const thumbnail = project.thumbnail || workflow?.thumbnail;
  // 后端列表接口不返回 workflow_data（性能优化），仅返回 node_count
  // 优先用 node_count；若已加载完整 workflow_data 则用其 nodes 长度；最后回退到本地 workflow
  const nodeCount = project.node_count ?? project.workflow_data?.nodes?.length ?? workflow?.nodes?.length ?? 0;

  return (
    <div
      onClick={() => onOpen(project.id)}
      className={`canvas-card group relative cursor-pointer overflow-hidden border transition-all ${menuOpen ? '-translate-y-1 shadow-xl shadow-black/20' : 'hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20'}`}
      style={{ backgroundColor: '#222222', borderColor: '#363636', borderRadius: '12px', height: '160px' }}
    >
      <div className="relative overflow-hidden h-full">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={project.name}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800/50 to-zinc-900/50">
            {getWorkflowIcon(project)}
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
        {isEditing ? (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onRenameSave(project.id);
                if (e.key === 'Escape') onRenameCancel();
              }}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500"
              autoFocus
            />
            <button
              onClick={() => onRenameSave(project.id)}
              className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
            >
              <Check size={14} />
            </button>
            <button
              onClick={onRenameCancel}
              className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-white truncate transition-colors">
                {project.name}
              </h3>
              <p className="text-xs text-zinc-300 mt-0.5">
                {formatRelativeTime(project.updated_at)} · {nodeCount} 个节点
              </p>
            </div>

            <div className="relative z-10" onClick={(e) => e.stopPropagation()}>
              <button
                ref={menuButtonRef}
                onClick={handleOpenMenu}
                className={`p-1.5 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800/80 transition-all ${menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                style={{ height: '28px', transform: 'translate(0px, 15px)' }}
              >
                <MoreVertical size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {menuOpen && createPortal(
        <>
          <div
            className="fixed inset-0"
            style={{ background: 'transparent', zIndex: 9998 }}
            onClick={() => setMenuOpen(false)}
          />
          <div
            className="fixed bg-zinc-800 border border-zinc-700 rounded-xl shadow-xl min-w-[120px] overflow-hidden"
            style={{ top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRenameStart(project.id, project.name);
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
            >
              <Pencil size={14} />
              重命名
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                setConfirmOpen(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
            >
              <Trash2 size={14} />
              删除
            </button>
          </div>
        </>,
        document.body
      )}

      {confirmOpen && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="bg-zinc-800 border border-zinc-700 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-medium text-white">确认删除画布</h3>
                <p className="text-sm text-zinc-400 mt-0.5">「{project.name}」将被永久删除，此操作无法撤销。</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 rounded-lg text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  onDelete(project.id);
                }}
                className="px-4 py-2 rounded-lg text-sm text-white bg-red-600 hover:bg-red-500 transition-colors"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// memo 默认浅比较：列表项，避免其他卡片重命名/搜索时全量重渲染
// 前提：CanvasGallery 传入的回调均已 useCallback 稳定化（id 回调签名）
export const CanvasCard = memo(CanvasCardComponent);

function getWorkflowIcon(project: Project) {
  const nodes = project.workflow_data?.nodes || [];
  const hasVideo = nodes.some((n: any) => n.type === 'VIDEO_GENERATOR');
  const hasAudio = nodes.some((n: any) => n.type === 'AUDIO_GENERATOR');
  const hasImage = nodes.some((n: any) => n.type === 'IMAGE_GENERATOR' || n.type === 'IMAGE_EDITOR');

  if (hasVideo) return <Film size={24} className="text-purple-400" />;
  if (hasAudio) return <Music size={24} className="text-emerald-400" />;
  if (hasImage) return <Image size={24} className="text-cyan-400" />;
  return <FolderOpen size={24} className="text-zinc-500" />;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  /* 后端历史数据/本地缓存可能是无时区标记的 UTC 串，new Date 会按本地时区误读（东八区恒定偏差 8 小时）：
     含时间部分且无 Z/偏移后缀时补 Z 再解析 */
  const normalized =
    /T/.test(dateStr) && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateStr) ? `${dateStr}Z` : dateStr;
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '';

  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  if (hours < 24) return `${hours}小时前`;
  if (days < 30) return `${days}天前`;
  return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
}
